import React, { useMemo, useState } from "react";

import {
    ResponsiveContainer,
    ComposedChart,
    Bar,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend
} from "recharts";

import "./CommunicationReport.css";


const CommunicationReport = ({
    hourlyData = [],
    daywiseData = []
}) => {

    const [viewMode, setViewMode] = useState("hourly");


    /* =========================================================
       NUMBER FORMAT
       ========================================================= */

    const formatNumber = (value) => {

        const number = Number(value || 0);

        return number.toLocaleString("en-IN");
    };


    /* =========================================================
       GET VALUE FROM DIFFERENT POSSIBLE API FIELD NAMES
       ========================================================= */

    const getValue = (item, ...keys) => {

        for (const key of keys) {

            if (
                item &&
                item[key] !== undefined &&
                item[key] !== null &&
                item[key] !== ""
            ) {
                return Number(item[key]) || 0;
            }
        }

        return 0;
    };


    /* =========================================================
       HOURLY DATA
       
       ALWAYS CREATE 24 HOURS
       00:00 -> 23:00

       Even if API gives only:
       00, 01, 02

       remaining hours will be 0.
       ========================================================= */

    const normalizedHourlyData = useMemo(() => {

        const apiData = Array.isArray(hourlyData)
            ? hourlyData
            : [];


        return Array.from(
            { length: 24 },
            (_, hour) => {

                const existingItem =
                    apiData.find((item) => {

                        const itemHour =
                            item?.hour ??
                            item?.Hour ??
                            item?.hours ??
                            item?.Hours;

                        if (
                            itemHour !== undefined &&
                            itemHour !== null
                        ) {
                            return (
                                Number(itemHour) === hour
                            );
                        }


                        const label =
                            item?.label ??
                            item?.time ??
                            item?.Time;


                        if (label !== undefined) {

                            const parsedHour =
                                parseInt(
                                    String(label)
                                        .split(":")[0],
                                    10
                                );

                            return (
                                parsedHour === hour
                            );
                        }

                        return false;
                    });


                return {

                    label:
                        `${String(hour).padStart(2, "0")}`,

                    fullLabel:
                        `${String(hour).padStart(2, "0")}:00`,

                    submit:
                        getValue(
                            existingItem,
                            "submit",
                            "Submit",
                            "totalSubmit",
                            "total_submit",
                            "submitCount",
                            "submit_count",
                            "submitCnt"
                        ),

                    delivered:
                        getValue(
                            existingItem,
                            "delivered",
                            "Delivered",
                            "totalDelivered",
                            "total_delivered",
                            "deliveredCount",
                            "delivered_count",
                            "deliveryCount",
                            "delivery_count"
                        ),

                    request:
                        getValue(
                            existingItem,
                            "request",
                            "Request",
                            "totalRequest",
                            "total_request",
                            "requestCount",
                            "request_count"
                        ),

                    rejected:
                        getValue(
                            existingItem,
                            "rejected",
                            "Rejected",
                            "totalRejected",
                            "total_rejected"
                        ),

                    failed:
                        getValue(
                            existingItem,
                            "failed",
                            "Failed",
                            "totalFailed",
                            "total_failed"
                        ),

                    awaited:
                        getValue(
                            existingItem,
                            "awaited",
                            "Awaited",
                            "totalAwaited",
                            "total_awaited"
                        )
                };
            }
        );

    }, [hourlyData]);


    /* =========================================================
       DAYWISE DATA
       
       ALWAYS CREATE LAST 30 DAYS
       INCLUDING TODAY

       Missing dates = 0
       ========================================================= */

   const normalizedDaywiseData = useMemo(() => {

    const apiData = Array.isArray(daywiseData)
        ? daywiseData
        : [];

    /*
     * Create lookup:
     *
     * "2026-09-12" -> complete object
     */
    const dataMap = new Map();

    apiData.forEach((item) => {

        if (!item?.label) {
            return;
        }

        /*
         * Since parent sends YYYY-MM-DD,
         * keep it exactly in that format.
         */
        const key = String(item.label).trim();

        dataMap.set(key, item);
    });


    /*
     * Today
     */
    const today = new Date();

    today.setHours(0, 0, 0, 0);


    /*
     * Generate exactly the last 30 days,
     * INCLUDING TODAY.
     *
     * Example if today = 2026-09-23:
     *
     * 2026-08-25
     * 2026-08-26
     * ...
     * 2026-09-23
     */
    return Array.from(
        { length: 30 },
        (_, index) => {

            const date = new Date(today);

            date.setDate(
                today.getDate() - (29 - index)
            );


            /*
             * Generate YYYY-MM-DD
             *
             * IMPORTANT:
             * We don't use toISOString()
             * because timezone conversion can
             * shift the date.
             */
            const year =
                date.getFullYear();

            const month =
                String(
                    date.getMonth() + 1
                ).padStart(2, "0");

            const day =
                String(
                    date.getDate()
                ).padStart(2, "0");


            const dateKey =
                `${year}-${month}-${day}`;


            /*
             * Find data passed from parent
             */
            const existingItem =
                dataMap.get(dateKey);


            /*
             * Display format:
             *
             * 25 Aug
             * 26 Aug
             * ...
             * 23 Sep
             */
            const displayLabel =
                date.toLocaleDateString(
                    "en-GB",
                    {
                        day: "2-digit",
                        month: "short"
                    }
                );


            return {

                /*
                 * X-axis label
                 */
                label: displayLabel,


                /*
                 * Actual YYYY-MM-DD
                 */
                date: dateKey,


                /*
                 * Values from parent.
                 *
                 * If there is no data for that date,
                 * value = 0.
                 */
                request:
                    Number(
                        existingItem?.request || 0
                    ),

                submit:
                    Number(
                        existingItem?.submit || 0
                    ),

                delivered:
                    Number(
                        existingItem?.delivered || 0
                    ),

                failed:
                    Number(
                        existingItem?.failed || 0
                    ),

                rejected:
                    Number(
                        existingItem?.rejected || 0
                    ),

                awaited:
                    Number(
                        existingItem?.awaited || 0
                    )
            };
        }
    );

}, [daywiseData]);


    /* =========================================================
       DATA USED BY GRAPH
       ========================================================= */

    const chartData =
        viewMode === "hourly"
            ? normalizedHourlyData
            : normalizedDaywiseData;


    /* =========================================================
       SUMMARY DATA
       
       IMPORTANT:
       Summary remains below the graph.
       It uses the supplied parent data.
       ========================================================= */

    const summary = useMemo(() => {

        const data =
            viewMode === "hourly"
                ? normalizedHourlyData
                : normalizedDaywiseData;


        const request =
            data.reduce(
                (total, item) =>
                    total +
                    Number(item.request || 0),
                0
            );


        const submit =
            data.reduce(
                (total, item) =>
                    total +
                    Number(item.submit || 0),
                0
            );


        const delivered =
            data.reduce(
                (total, item) =>
                    total +
                    Number(item.delivered || 0),
                0
            );


        const failed =
            data.reduce(
                (total, item) =>
                    total +
                    Number(item.failed || 0),
                0
            );


        const rejected =
            data.reduce(
                (total, item) =>
                    total +
                    Number(item.rejected || 0),
                0
            );


        const awaited =
            data.reduce(
                (total, item) =>
                    total +
                    Number(item.awaited || 0),
                0
            );


        /*
         * If API doesn't provide request/rejected/awaited,
         * don't let the graph disappear.
         *
         * For rejected:
         * Request - Submit
         */

        const finalRejected =
            rejected > 0
                ? rejected
                : Math.max(
                      0,
                      request - submit
                  );


        /*
         * If awaited is not supplied,
         * calculate it.
         */

        const finalAwaited =
            awaited > 0
                ? awaited
                : Math.max(
                      0,
                      submit -
                          delivered -
                          failed
                  );


        return {

            request,

            rejected:
                finalRejected,

            submit,

            delivered,

            failed,

            awaited:
                finalAwaited
        };

    }, [
        viewMode,
        normalizedHourlyData,
        normalizedDaywiseData
    ]);


    /* =========================================================
       PERCENTAGE
       ========================================================= */

    const getPercentage = (value) => {

        if (
            !summary.request ||
            summary.request === 0
        ) {
            return "0%";
        }


        return (
            (
                Number(value) /
                Number(summary.request)
            ) *
            100
        ).toFixed(1) + "%";
    };


    /* =========================================================
       DATE DISPLAY
       ========================================================= */

    const today = new Date();


    const formattedToday =
        today.toLocaleDateString(
            "en-GB",
            {
                day: "2-digit",
                month: "short",
                year: "numeric"
            }
        );


    const firstDate =
        new Date(today);


    firstDate.setDate(
        today.getDate() - 29
    );


    const formattedFirstDate =
        firstDate.toLocaleDateString(
            "en-GB",
            {
                day: "2-digit",
                month: "short"
            }
        );


    /* =========================================================
       CUSTOM TOOLTIP
       
       ONLY:
       Submit Count
       Delivered Count
       ========================================================= */

    const CustomTooltip = ({
        active,
        payload,
        label
    }) => {

        if (
            !active ||
            !payload ||
            !payload.length
        ) {
            return null;
        }


        const submitPayload =
            payload.find(
                item =>
                    item.dataKey ===
                    "submit"
            );


        const deliveredPayload =
            payload.find(
                item =>
                    item.dataKey ===
                    "delivered"
            );


        return (

            <div className="communication-tooltip">

                <div className="communication-tooltip-title">
                    {viewMode === "hourly"
                        ? `${label}:00`
                        : label}
                </div>


                <div className="communication-tooltip-row">

                    <span className="tooltip-label">

                        <span
                            className="tooltip-dot"
                            style={{
                                backgroundColor:
                                    "#168FC5"
                            }}
                        />

                        Submit Count

                    </span>


                    <strong>
                        {formatNumber(
                            submitPayload?.value
                        )}
                    </strong>

                </div>


                <div className="communication-tooltip-row">

                    <span className="tooltip-label">

                        <span
                            className="tooltip-dot"
                            style={{
                                backgroundColor:
                                    "#F28C18"
                            }}
                        />

                        Delivered Count

                    </span>


                    <strong>
                        {formatNumber(
                            deliveredPayload?.value
                        )}
                    </strong>

                </div>

            </div>
        );
    };


    /* =========================================================
       RENDER
       ========================================================= */

    return (

        <div className="communication-report">


            {/* =================================================
               HEADER
               ================================================= */}

            <div className="communication-report-header">


                <div className="communication-report-title-area">

                    <div className="communication-report-icon">
                        <span>▥</span>
                    </div>


                    <div className="communication-report-heading">

                        <div className="communication-report-title">
                            Traffic Analysis Report
                        </div>


                        <div className="communication-report-subtitle">

                            {viewMode === "hourly"

                                ? `Hourly activity • ${formattedToday}`

                                : `Daily activity • ${formattedFirstDate} - ${formattedToday}`

                            }

                        </div>

                    </div>

                </div>


                {/* =================================================
                   TOGGLE
                   ================================================= */}

                <div className="report-toggle">

                    <button
                        type="button"
                        className={
                            viewMode === "hourly"
                                ? "report-toggle-button active"
                                : "report-toggle-button"
                        }
                        onClick={() =>
                            setViewMode("hourly")
                        }
                    >

                        <span className="toggle-icon">
                            ◷
                        </span>

                        Hourly

                    </button>


                    <button
                        type="button"
                        className={
                            viewMode === "daywise"
                                ? "report-toggle-button active"
                                : "report-toggle-button"
                        }
                        onClick={() =>
                            setViewMode("daywise")
                        }
                    >

                        <span className="toggle-icon">
                            ▣
                        </span>

                        Daywise

                    </button>

                </div>

            </div>

<div className="communication-chart-legend">

        <div className="chart-legend-item">

            <span
                className="chart-legend-circle submit"
            />

            <span>
                Submit Count
            </span>

        </div>


        <div className="chart-legend-item">

            <span
                className="chart-legend-circle delivered"
            />

            <span>
                Delivered Count
            </span>

        </div>

    </div>

            {/* =================================================
               GRAPH
               ================================================= */}

            <div className="communication-chart-wrapper">

                <ResponsiveContainer
                    width="100%"
                    height="100%"
                >

                    <ComposedChart
                        data={chartData}
                        margin={{
                            top: 10,
                            right: 12,
                            left: 0,
                            bottom: 5
                        }}
                    >


                        {/* GRID */}

                        <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                            stroke="#E4EAEE"
                        />


                        {/* X AXIS */}

                        <XAxis
                            dataKey="label"

                            tick={{
                                fontSize:
                                    viewMode ===
                                    "hourly"
                                        ? 10
                                        : 10,

                                fill: "#617582"
                            }}

                            axisLine={{
                                stroke:
                                    "#D7E1E6"
                            }}

                            tickLine={false}

                            interval={
                                viewMode ===
                                "hourly"
                                    ? 0
                                    : 0
                            }

                            angle={
                                viewMode ===
                                "daywise"
                                    ? -35
                                    : 0
                            }

                            textAnchor={
                                viewMode ===
                                "daywise"
                                    ? "end"
                                    : "middle"
                            }

                            height={
                                viewMode ===
                                "daywise"
                                    ? 58
                                    : 30
                            }
                        />


                        {/* Y AXIS */}

                        <YAxis
                            tick={{
                                fontSize: 10,
                                fill: "#617582"
                            }}

                            axisLine={false}

                            tickLine={false}

                            width={48}

                            tickFormatter={
                                (value) => {

                                    if (
                                        value >=
                                        1000000
                                    ) {
                                        return (
                                            (
                                                value /
                                                1000000
                                            ).toFixed(
                                                1
                                            ) + "M"
                                        );
                                    }


                                    if (
                                        value >=
                                        1000
                                    ) {
                                        return (
                                            (
                                                value /
                                                1000
                                            ).toFixed(
                                                value >=
                                                    100000
                                                    ? 0
                                                    : 1
                                            ) + "K"
                                        );
                                    }


                                    return value;
                                }
                            }
                        />


                        {/* TOOLTIP */}

                        <Tooltip
                            content={
                                <CustomTooltip />
                            }

                            cursor={{
                                fill:
                                    "rgba(21,55,72,0.04)"
                            }}
                        />


                        {/* =================================================
                           LEGEND
                           
                           ONLY SUBMIT + DELIVERED
                           ================================================= */}

                        {/* <Legend
                            verticalAlign="top"
                            align="right"
                            height={35}
                            iconType="circle"

                            wrapperStyle={{
                                fontSize: "11px",
                                color: "#29495A",
                                paddingBottom:
                                    "5px"
                            }}

                            formatter={(
                                value
                            ) => {

                                if (
                                    value ===
                                    "Total Submit"
                                ) {
                                    return "Submit Count";
                                }

                                if (
                                    value ===
                                    "Total Delivered"
                                ) {
                                    return "Delivered Count";
                                }

                                return value;
                            }}
                        /> */}


                        {/* =================================================
                           SUBMIT BAR
                           ================================================= */}

                        <Bar
                            dataKey="submit"

                            name="Total Submit"

                            fill="#168FC5"

                            radius={[
                                4,
                                4,
                                0,
                                0
                            ]}

                            barSize={
                                viewMode ===
                                "hourly"
                                    ? 12
                                    : 14
                            }

                            isAnimationActive={false}
                        />


                        {/* =================================================
                           DELIVERED LINE
                           
                           ONLY LINE
                           ================================================= */}

                        <Line
                            type="monotone"

                            dataKey="delivered"

                            name="Total Delivered"

                            stroke="#F28C18"

                            strokeWidth={3}

                            dot={{
                                r:
                                    viewMode ===
                                    "hourly"
                                        ? 3
                                        : 3.5,

                                fill:
                                    "#FFFFFF",

                                stroke:
                                    "#F28C18",

                                strokeWidth: 2
                            }}

                            activeDot={{
                                r: 5,

                                fill:
                                    "#F28C18",

                                stroke:
                                    "#FFFFFF",

                                strokeWidth: 2
                            }}

                            isAnimationActive={false}
                        />

                    </ComposedChart>

                </ResponsiveContainer>

            </div>


            {/* =================================================
               SUMMARY CARDS
               ================================================= */}

            <div className="communication-summary">


                {/* TOTAL REQUEST */}

                <div className="summary-item">

                    <span className="summary-label">
                        Total Request
                    </span>

                    <strong>
                        {formatNumber(
                            summary.request
                        )}
                    </strong>

                    <span className="summary-percent">
                        100%
                    </span>

                </div>


                {/* TOTAL REJECTED */}

                <div className="summary-item">

                    <span className="summary-label">
                        Total Rejected
                    </span>

                    <strong>
                        {formatNumber(
                            summary.rejected
                        )}
                    </strong>

                    <span className="summary-percent failed">
                        {getPercentage(
                            summary.rejected
                        )}
                    </span>

                </div>


                {/* TOTAL SUBMIT */}

                <div className="summary-item">

                    <span className="summary-label">
                        Total Submit
                    </span>

                    <strong>
                        {formatNumber(
                            summary.submit
                        )}
                    </strong>

                    <span className="summary-percent">
                        {getPercentage(
                            summary.submit
                        )}
                    </span>

                </div>


                {/* TOTAL DELIVERED */}

                <div className="summary-item">

                    <span className="summary-label">
                        Total Delivered
                    </span>

                    <strong>
                        {formatNumber(
                            summary.delivered
                        )}
                    </strong>

                    <span className="summary-percent delivered">
                        {getPercentage(
                            summary.delivered
                        )}
                    </span>

                </div>


                {/* TOTAL FAILED */}

                <div className="summary-item">

                    <span className="summary-label">
                        Total Failed
                    </span>

                    <strong>
                        {formatNumber(
                            summary.failed
                        )}
                    </strong>

                    <span className="summary-percent failed">
                        {getPercentage(
                            summary.failed
                        )}
                    </span>

                </div>


                {/* TOTAL AWAITED */}

                <div className="summary-item">

                    <span className="summary-label">
                        Total Awaited
                    </span>

                    <strong>
                        {formatNumber(
                            summary.awaited
                        )}
                    </strong>

                    <span className="summary-percent">
                        {getPercentage(
                            summary.awaited
                        )}
                    </span>

                </div>

            </div>

        </div>
    );
};


export default CommunicationReport;